import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Chip,
  Box,
  LinearProgress,
  CircularProgress,
  Link,
} from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircle';
import Error from '@material-ui/icons/Error';
import Warning from '@material-ui/icons/Warning';
import Info from '@material-ui/icons/Info';
import Favorite from '@material-ui/icons/Favorite';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { HealthDataService, HealthMetric } from '../../services/healthDataService';
import { GITLAB_TOKEN } from '../../config/environment';

// Removed unused interface

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'healthy':
      return <CheckCircle style={{ color: '#4caf50' }} />;
    case 'warning':
      return <Warning style={{ color: '#ff9800' }} />;
    case 'error':
      return <Error style={{ color: '#f44336' }} />;
    default:
      return <Info style={{ color: '#2196f3' }} />;
  }
};

const getProgressValue = (status: string): number => {
  if (status === 'healthy') return 100;
  if (status === 'warning') return 70;
  if (status === 'error') return 10;
  if (status === 'info') return 50;    
  return 30;
};

// Removed unused function

const EntityHealthCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [healthMetrics, setHealthMetrics] = useState<HealthMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Extract health information from entity annotations
  const healthCheckUrl = entity.metadata.annotations?.['backstage.io/health-check'];
  const sonarQubeUrl = entity.metadata.annotations?.['sonarqube.org/project-key'];
  const sonarQubeInstance = entity.metadata.annotations?.['sonarqube.org/instance'];
  const trivyScan = entity.metadata.annotations?.['backstage.io/trivy-scan'];
  const trivyConfig = entity.metadata.annotations?.['backstage.io/trivy-config'];
  const linterConfigured = entity.metadata.annotations?.['backstage.io/linter-configured'];
  const linterEslint = entity.metadata.annotations?.['backstage.io/linter-eslint'];
  const linterPrettier = entity.metadata.annotations?.['backstage.io/linter-prettier'];
  const securityReportsConfig = entity.metadata.annotations?.['backstage.io/security-reports'];
  // Get GitLab project ID from entity annotations (supports both gitlab.com and custom GitLab hosts)
  const gitlabProjectId = entity.metadata.annotations?.['gitlab.com/project-id'] || 
                          entity.metadata.annotations?.['gitlab/git.agilie.com/project-id'];

  // Initialize health data service
  // SonarQube token is now handled securely on backend, no need to pass it from frontend
  const gitlabToken = GITLAB_TOKEN;
  
  // Memoize the health service to prevent recreation on every render
  // SonarQube token is handled securely on backend side
  const healthService = useMemo(
    () => new HealthDataService(gitlabToken, undefined, discoveryApi, fetchApi),
    [gitlabToken, discoveryApi, fetchApi]
  );


  // Fetch real health data
  useEffect(() => {
    const fetchHealthData = async () => {
      // Prevent multiple simultaneous requests
      if (fetchingRef.current) {
        return;
      }
      
      fetchingRef.current = true;
      setLoading(true);
      
      // Set a timeout to prevent infinite loading
      timeoutRef.current = setTimeout(() => {
        setLoading(false);
        fetchingRef.current = false;
      }, 10000); // 10 second timeout
      
      try {
        const metrics: HealthMetric[] = [];

        if (!gitlabProjectId) {
          // No project ID configured in catalog-info.yaml - return empty metrics
          setHealthMetrics([]);
          setLoading(false);
          fetchingRef.current = false;
          return;
        }
        
        // REAL DATA for supported components
        
        // Get real pipeline status
        const pipelineStatus = await healthService.getGitLabPipelineStatus(gitlabProjectId);
        metrics.push(pipelineStatus);
        
        // Get real test coverage
        const testCoverage = await healthService.getTestCoverage(gitlabProjectId);
        metrics.push(testCoverage);
        
        // Get real build status
        const buildStatus = await healthService.getBuildStatus(gitlabProjectId);
        metrics.push(buildStatus);

        // Get real npm audit security status
        const securityAudit = await healthService.getNpmAuditStatus(gitlabProjectId, securityReportsConfig);
        metrics.push(securityAudit);

        // Get Trivy environment scan if configured
        if (trivyScan === 'enabled') {
          const trivyStatus = await healthService.getTrivyScanStatus(gitlabProjectId, trivyConfig);
          metrics.push(trivyStatus);
        }

        // Get SonarQube data if configured
        // Using official @backstage-community/plugin-sonarqube-backend plugin
        if (sonarQubeUrl) {
          const sonarData = await healthService.getSonarQubeMetrics(sonarQubeUrl, sonarQubeInstance);
          metrics.push(sonarData);
        }

        // Add linters checkmark if configured
        if (linterConfigured === 'true') {
          const lintersList = [];
          if (linterEslint === 'enabled') lintersList.push('ESLint');
          if (linterPrettier === 'enabled') lintersList.push('Prettier');
          
          metrics.push({
            name: 'Linters',
            value: lintersList.length > 0 ? `✅ ${lintersList.join(', ')}` : '✅ Configured',
            status: 'healthy',
            description: lintersList.length > 0 
              ? `🔄 Linters configured: ${lintersList.join(', ')}`
              : '🔄 Linters configured and active',
          });
        }

        setHealthMetrics(metrics);
      } catch (error) {
        // console.error('Error fetching health data:', error);
        // Return empty metrics on error
        setHealthMetrics([]);
      } finally {
        // Clear timeout if request completes before timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchHealthData();
    
    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      fetchingRef.current = false;
    };
  }, [entity.metadata.name, entity.metadata.annotations, sonarQubeUrl, sonarQubeInstance, healthService, gitlabToken, trivyScan, trivyConfig, linterConfigured, linterEslint, linterPrettier, securityReportsConfig, gitlabProjectId]);


  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginLeft: 16 }}>
              Loading real health data from {entity.metadata.name}...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
          <CardHeader
            title={
              <Box display="flex" alignItems="center">
                <Favorite style={{ marginRight: 8 }} />
                <Typography variant="h6">
                  Project Health Status
                </Typography>
                <Box ml={2}>
                  {getStatusIcon('healthy')}
                </Box>
              </Box>
            }
          />
      <CardContent>
        <Grid container spacing={2}>
          {healthMetrics.length === 0 ? (
            <Grid item xs={12}>
              <Box p={3} textAlign="center">
                <Typography variant="body1" color="textSecondary">
                  No health data available for this component. 
                  {!gitlabProjectId && (
                    <> Please add <code>gitlab.com/project-id</code> annotation to catalog-info.yaml</>
                  )}
                </Typography>
              </Box>
            </Grid>
          ) : (
            healthMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                p={2}
                border={1}
                borderColor="grey.300"
                borderRadius={1}
                bgcolor="grey.50"
              >
                <Box display="flex" alignItems="center" mb={1}>
                  {getStatusIcon(metric.status)}
                  <Typography variant="subtitle2" style={{ marginLeft: 8, color: '#000000', fontWeight: 'bold' }}>
                    {metric.name}
                  </Typography>
                </Box>
                <Typography variant="h6" style={{ color: '#000000', fontWeight: 'bold' }}>
                  {metric.value}
                </Typography>
                <Typography variant="caption" style={{ color: '#000000' }}>
                  {metric.description}
                </Typography>
                {metric.reportUrls && metric.reportUrls.length > 0 && (
                  <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                    {metric.reportUrls.map((report, reportIndex) => (
                      <Link
                        key={reportIndex}
                        href={report.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        style={{ cursor: 'pointer', textDecoration: 'underline', display: 'flex', alignItems: 'center', color: '#0a4efe' }}
                      >
                        {report.title} <OpenInNew style={{ fontSize: 12, marginLeft: 4 }} />
                      </Link>
                    ))}
                  </Box>
                )}
                <Box mt={1}>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressValue(metric.status)}
                    style={{
                      backgroundColor: '#e0e0e0',
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            ))
          )}
        </Grid>


            {/* Data Source Legend */}
            {healthMetrics.length > 0 && (
              <Box mt={3} p={2} bgcolor="grey.100" borderRadius={1}>
                <Typography variant="subtitle2" gutterBottom>
                  📊 Data Sources & Configuration:
                </Typography>
                <Typography variant="body2" style={{ color: '#000000' }}>
                  • <strong>Pipeline Status</strong>: 🔄 Real data from GitLab API (Project ID: {gitlabProjectId || 'N/A'})
                  • <strong>Test Coverage</strong>: 🔄 Real data from GitLab CI/CD artifacts
                  • <strong>Build Status</strong>: 🔄 Real data from GitLab pipelines
                  • <strong>Security Audit</strong>: 🔄 Real data from npm audit via GitLab CI/CD
                  {sonarQubeUrl && ' • <strong>SonarQube</strong>: 🔄 Real data from SonarQube API'}
                  {trivyScan === 'enabled' && ' • <strong>Trivy Scan</strong>: 🔄 Real data from Trivy via GitLab CI/CD'}
                  {linterConfigured === 'true' && ' • <strong>Linters</strong>: ✅ Configured'}
                </Typography>
                <Typography variant="caption" style={{ marginTop: 8, display: 'block', color: '#000000' }}>
                  💡 Real data sources: GitLab CI/CD, SonarQube. Configure additional monitoring tools for complete coverage.
                </Typography>
              </Box>
            )}

        {/* SonarQube Integration - Display real data from metrics */}
        {sonarQubeUrl && (() => {
          const sonarMetric = healthMetrics.find(m => m.name === 'SonarQube');
          if (!sonarMetric) return null;
          
          // Parse description to extract values
          const description = sonarMetric.description || '';
          const coverageMatch = description.match(/Coverage:\s*([\d.]+)%?/);
          const duplicationsMatch = description.match(/Duplications:\s*([\d.]+)%?/);
          const statusMatch = description.match(/Status:\s*(\w+)/);
          
          const coverage = coverageMatch ? coverageMatch[1] : 'N/A';
          const duplications = duplicationsMatch ? duplicationsMatch[1] : 'N/A';
          const qualityGateStatus = sonarMetric.value || statusMatch?.[1] || 'N/A';
          
          // Determine chip color based on status
          let chipColor: 'primary' | 'default' | 'secondary' = 'primary';
          if (sonarMetric.status === 'warning') {
            chipColor = 'default';
          } else if (sonarMetric.status === 'error') {
            chipColor = 'secondary';
          }
          
          return (
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                SonarQube Analysis
              </Typography>
              <Grid container spacing={2}>
                <Grid item>
                  <Chip
                    label={`Project Key: ${sonarQubeUrl}`}
                    color="primary"
                    variant="outlined"
                    clickable
                    onClick={() => window.open(`https://${sonarQubeInstance || 'sonarqube.dev2.scrij.com'}/dashboard?id=${sonarQubeUrl}`, '_blank')}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={`Quality Gate: ${qualityGateStatus}`}
                    color={chipColor}
                    variant="outlined"
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={`Coverage: ${coverage}%`}
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={`Duplications: ${duplications}%`}
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })()}

        {/* Health Check Links */}
        {healthCheckUrl && (
          <Box mt={3} p={2} bgcolor="info.light" borderRadius={1}>
            <Typography variant="h6" gutterBottom>
              Health Monitoring
            </Typography>
            <Typography variant="body2">
              Real-time health checks are available at:{' '}
              <a href={healthCheckUrl} target="_blank" rel="noopener noreferrer">
                {healthCheckUrl}
              </a>
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EntityHealthCard;
